import Phaser from 'phaser';
import { WORLD_HEIGHT, WORLD_WIDTH } from '../config';
import type { WorldState } from '../sim/types';
import type { TrackView, WallView } from '../sim/world';
import type { GameHarness, Renderer } from '../game/harness';
import { DebugOverlay, type DebugFlags } from './debug/DebugOverlay';
import { rectCorners } from './geom';

const CAR_COLORS = [0xff5252, 0x4fc3f7, 0xffd54f, 0x81c784, 0xba68c8, 0xff8a65];

export interface GameSceneOptions {
  /** Caméra qui suit la voiture (jeu) ; sinon vue d'ensemble (tests). */
  follow?: boolean;
}

/**
 * Couche de rendu Phaser. Elle ne fait QUE dessiner l'état que lui passe le harness.
 * Le pas fixe est piloté depuis update() via harness.advance().
 */
export class GameScene extends Phaser.Scene implements Renderer {
  private readonly harness: GameHarness;
  private readonly follow: boolean;
  private readonly carGfx: Phaser.GameObjects.Graphics[] = [];
  private bg!: Phaser.GameObjects.Rectangle;
  private trackGfx!: Phaser.GameObjects.Graphics;
  private skidGfx!: Phaser.GameObjects.Graphics;
  private wallsGfx!: Phaser.GameObjects.Graphics;
  private overlay!: DebugOverlay;
  private uiCamera?: Phaser.Cameras.Scene2D.Camera;
  /** Segments de trace de pneu (espace monde), avec leur opacité résiduelle. */
  private skidMarks: { x1: number; y1: number; x2: number; y2: number; a: number }[] = [];
  /** Dernière position des roues arrière par voiture, tant qu'elle dérobe (sinon null). */
  private skidLast: (null | { lx: number; ly: number; rx: number; ry: number })[] = [];
  /** Dernier `step` vu — sert à détecter un reset de monde (step qui repart) pour purger les traces. */
  private skidLastStep = 0;

  constructor(harness: GameHarness, options: GameSceneOptions = {}) {
    super('GameScene');
    this.harness = harness;
    this.follow = options.follow ?? false;
  }

  create(): void {
    this.cameras.main.setBackgroundColor('#1b5e20');
    this.game.canvas.id = 'game-canvas';

    this.bg = this.add
      .rectangle(WORLD_WIDTH / 2, WORLD_HEIGHT / 2, WORLD_WIDTH, WORLD_HEIGHT, 0x2e7d32)
      .setDepth(0);
    this.trackGfx = this.add.graphics().setDepth(1);
    this.skidGfx = this.add.graphics().setDepth(2); // décals sur la route, sous murs et voitures
    this.wallsGfx = this.add.graphics().setDepth(5);
    this.ensureCarGfx(this.harness.world.cars.length);
    this.overlay = new DebugOverlay(this);

    // Caméra UI dédiée : fixe et SANS zoom. Le HUD et la légende des touches restent
    // donc lisibles à l'écran même quand la caméra principale zoome et suit la voiture.
    // (setScrollFactor(0) bloque le défilement mais PAS le zoom → il faut une 2e caméra.)
    this.uiCamera = this.cameras.add(0, 0, WORLD_WIDTH, WORLD_HEIGHT);
    this.cameras.main.ignore(this.overlay.uiObjects); // la principale ne rend pas l'UI
    this.uiCamera.ignore([this.bg, this.trackGfx, this.skidGfx, this.wallsGfx, this.overlay.worldLayer, ...this.carGfx]);

    if (this.follow && this.carGfx.length > 0) {
      this.cameras.main.setBounds(0, 0, WORLD_WIDTH, WORLD_HEIGHT);
      this.cameras.main.setZoom(1.7);
      this.cameras.main.startFollow(this.carGfx[0], false, 0.1, 0.1);
    }

    this.harness.renderer = this;
    this.harness.markReady();
    this.harness.renderFromState();
  }

  private ensureCarGfx(count: number): void {
    while (this.carGfx.length < count) {
      const g = this.add.graphics().setDepth(10);
      this.carGfx.push(g);
      this.uiCamera?.ignore(g); // la caméra UI ne rend jamais le monde-jeu
    }
  }

  private drawTrack(track: TrackView | null): void {
    const g = this.trackGfx;
    g.clear();
    if (!track) return;
    const n = track.centerline.length;
    // Bitume (quads entre bord intérieur et extérieur).
    g.fillStyle(0x37474f, 1);
    for (let i = 0; i < n; i++) {
      const j = (i + 1) % n;
      g.fillPoints(
        [
          new Phaser.Math.Vector2(track.inner[i].x, track.inner[i].y),
          new Phaser.Math.Vector2(track.outer[i].x, track.outer[i].y),
          new Phaser.Math.Vector2(track.outer[j].x, track.outer[j].y),
          new Phaser.Math.Vector2(track.inner[j].x, track.inner[j].y),
        ],
        true,
      );
    }
    // Bordures.
    g.lineStyle(3, 0xeceff1, 0.9);
    g.strokePoints(track.inner.map((p) => new Phaser.Math.Vector2(p.x, p.y)), true);
    g.strokePoints(track.outer.map((p) => new Phaser.Math.Vector2(p.x, p.y)), true);
    // Ligne d'arrivée (porte 0).
    const finish = track.gates[0];
    g.lineStyle(6, 0xffffff, 1);
    g.lineBetween(finish.ax, finish.ay, finish.bx, finish.by);
  }

  private drawWalls(walls: WallView[]): void {
    const g = this.wallsGfx;
    g.clear();
    for (const w of walls) {
      g.fillStyle(0x6d4c41, 1);
      g.lineStyle(2, 0x3e2723, 1);
      const pts = rectCorners(w.x, w.y, w.w, w.h, w.angle);
      g.fillPoints(pts, true);
      g.strokePoints(pts, true);
    }
  }

  /**
   * Traces de pneu : tant qu'une voiture dérobe (grip bas + en mouvement), on relie la position
   * de ses roues arrière d'un PAS DE SIM au suivant. L'échantillonnage et le fondu sont indexés
   * sur les pas de sim (60 Hz), pas sur les frames de rendu → durée stable quel que soit l'écran.
   * Purement visuel (n'écrit aucun état de sim).
   */
  private updateSkidmarks(state: WorldState, on: boolean): void {
    const g = this.skidGfx;
    const advanced = state.step - this.skidLastStep; // nb de pas de sim depuis le dernier rendu
    this.skidLastStep = state.step;
    if (advanced < 0) {
      // Reset de monde (changement de circuit/véhicule via le menu) : le step repart → on purge.
      this.skidMarks.length = 0;
      this.skidLast.length = 0;
    }
    if (!on) {
      this.skidMarks.length = 0;
      this.skidLast.length = 0; // ne pas garder de position périmée (sinon traînée parasite au ré-activage)
      g.clear();
      return;
    }

    if (advanced > 0) {
      for (let i = 0; i < state.cars.length; i++) {
        const c = state.cars[i];
        const slipping = c.grip < 0.65 && c.speed > 30;
        if (!slipping) {
          this.skidLast[i] = null;
          continue;
        }
        const fx = Math.cos(c.angle);
        const fy = Math.sin(c.angle);
        const back = -c.w * 0.32; // essieu arrière
        const lat = c.h * 0.4; // demi-voie
        const w = {
          lx: c.x + fx * back - -fy * lat,
          ly: c.y + fy * back - fx * lat,
          rx: c.x + fx * back + -fy * lat,
          ry: c.y + fy * back + fx * lat,
        };
        const prev = this.skidLast[i];
        if (prev) {
          this.skidMarks.push({ x1: prev.lx, y1: prev.ly, x2: w.lx, y2: w.ly, a: 1 });
          this.skidMarks.push({ x1: prev.rx, y1: prev.ry, x2: w.rx, y2: w.ry, a: 1 });
        }
        this.skidLast[i] = w;
      }
      const fade = 0.012 * advanced; // ~1,4 s de vie (≈ 83 pas), indépendant du taux de rendu
      for (const m of this.skidMarks) m.a -= fade;
      if (this.skidMarks.some((m) => m.a <= 0)) this.skidMarks = this.skidMarks.filter((m) => m.a > 0);
      if (this.skidMarks.length > 900) this.skidMarks.splice(0, this.skidMarks.length - 900);
    }

    g.clear();
    for (const m of this.skidMarks) {
      g.lineStyle(3, 0x141414, Math.min(0.45, m.a * 0.45));
      g.lineBetween(m.x1, m.y1, m.x2, m.y2);
    }
  }

  renderState(state: WorldState, debug: DebugFlags): void {
    const walls = this.harness.world.getWalls();
    const track = this.harness.world.getTrack();
    this.drawTrack(track);
    this.drawWalls(walls);
    this.updateSkidmarks(state, debug.skidmarks);
    const shapes = debug.collisionBodies ? this.harness.world.getCollisionShapes() : null;

    this.ensureCarGfx(state.cars.length);
    for (let i = 0; i < state.cars.length; i++) {
      const c = state.cars[i];
      const g = this.carGfx[i];
      g.clear();
      g.setPosition(c.x, c.y);
      g.setRotation(c.angle);
      const hw = c.w / 2;
      const hh = c.h / 2;
      const color = CAR_COLORS[i % CAR_COLORS.length];
      g.fillStyle(color, 1);
      g.fillRoundedRect(-hw, -hh, c.w, c.h, Math.min(4, hh));
      // Repère avant : petit triangle blanc pointant vers le cap (+x).
      g.fillStyle(0xffffff, 1);
      g.fillTriangle(hw - 9, -hh * 0.6, hw - 9, hh * 0.6, hw, 0);
    }
    // Efface les graphismes de voitures en trop (ex. passage Course→Libre : moins de voitures).
    for (let i = state.cars.length; i < this.carGfx.length; i++) this.carGfx[i].clear();

    const aiTargets = debug.racingLine ? this.harness.world.getAiTargets() : [];
    this.overlay.draw(state, debug, track, shapes, aiTargets);
  }

  override update(_time: number, delta: number): void {
    this.harness.advance(delta);
    this.harness.renderFromState();
  }
}
